import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ChevronDownIcon, ChevronRightIcon, EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { type DigestSection, DragItemType, SECTION_COLORS } from "@utils/types";
import { useEffect, useRef, useState } from "react";

import { Badge } from "@fronterahealth/frontera-ui-components";

import { type GetCommunityPostsOutput } from "@api/rest/types-and-hooks";
import { GripHandle } from "@components/GripHandle/GripHandle";
import { InlineEdit } from "@components/InlineEdit/InlineEdit";
import { PostSearchDropdown } from "@components/PostSearchDropdown/PostSearchDropdown";
import { SortableItem } from "@components/SortableItem/SortableItem";

import { PostRow } from "./PostRow";

interface SectionCardProps {
  section: DigestSection;
  index: number;
  allSelectedPostIds: number[];
  dragHandleProps: Record<string, unknown>;
  onToggleCollapse: () => void;
  onRenameHeader: (val: string) => void;
  onAddPost: (post: GetCommunityPostsOutput) => void;
  onRemovePost: (postId: number) => void;
  onDelete: () => void;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  section,
  index,
  allSelectedPostIds,
  dragHandleProps,
  onToggleCollapse,
  onRenameHeader,
  onAddPost,
  onRemovePost,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const sectionColor = section.color ?? SECTION_COLORS[index % SECTION_COLORS.length];

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  return (
    <div className="rounded-xl border border-border-medium bg-bg-surface">
      <div className="flex items-center gap-x-2 px-4 py-3">
        <button
          {...dragHandleProps}
          onClick={(e) => e.stopPropagation()}
          className="text-text-tertiary shrink-0 cursor-grab active:cursor-grabbing touch-none"
          aria-label="Drag to reorder section"
        >
          <GripHandle />
        </button>
        <button onClick={onToggleCollapse} className="text-icon-secondary hover:text-icon-primary">
          {section.collapsed ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
        </button>
        <InlineEdit value={section.header} onChange={onRenameHeader} placeholder="Section title..." />
        <Badge text={`${section.posts.length} ${section.posts.length === 1 ? "post" : "posts"}`} appearance="neutral" />
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="text-icon-tertiary hover:text-icon-primary p-1 rounded"
          >
            <EllipsisVerticalIcon className="h-4 w-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-6 z-20 min-w-32 rounded-lg border border-border-medium bg-bg-surface shadow-lg overflow-hidden">
              <button
                className="w-full text-left px-3 py-2 text-sm text-text-error-primary hover:bg-bg-hovered"
                onMouseDown={() => {
                  onDelete();
                  setMenuOpen(false);
                }}
              >
                Delete section
              </button>
            </div>
          )}
        </div>
      </div>

      {!section.collapsed && (
        <div className="flex flex-col gap-y-2 px-4 pb-4">
          <SortableContext items={section.posts.map((p) => p.id.toString())} strategy={verticalListSortingStrategy}>
            {section.posts.map((post) => (
              <SortableItem
                key={post.id}
                id={post.id.toString()}
                data={{ type: DragItemType.POST, sectionId: section.id }}
              >
                {(postDragHandleProps) => (
                  <PostRow
                    post={post}
                    accentColor={sectionColor}
                    dragHandleProps={postDragHandleProps}
                    onRemove={() => onRemovePost(post.id)}
                  />
                )}
              </SortableItem>
            ))}
          </SortableContext>
          <PostSearchDropdown
            placeholder="Search and add a community post..."
            excludePostIds={allSelectedPostIds}
            onSelect={onAddPost}
          />
        </div>
      )}
    </div>
  );
};
